import React from 'react';
import { addMinutes } from 'date-fns';
import BuildIcon from '@material-ui/icons/Build';
import { makeStyles } from '@material-ui/core/styles';
import DisplaySelectedUnitTimeFrame from '@components/SchedulePage/DisplaySelectedUnitTimeFrame/DisplaySelectedUnitTimeFrame';
import { useBlockEventConfig } from '@components/SchedulePage/utilities/useBlockEventConfig/useBlockEventConfig';
import CopyText from '@components/CopyText/CopyText';
import { ManageScheduleEventMenu } from '@components/SchedulePage/ManageScheduleEventMenu/ManageScheduleEventMenu';
import { SurgeonEventProps } from '@components/SchedulePage/SurgeonEvent/SurgeonEventProps';
import {
	SchedulingCancelCaseModal
} from '@components/SchedulePage/ManageScheduleEventMenu/SchedulingCancelCaseModal/SchedulingCancelCaseModal';
import {
	CaseLockModal
} from '@components/CaseDetails/ManageCaseDropDown/DropDownOptions/EditCase/CaseLockModal/CaseLockModal';
import {
	SchedulingCopyCaseModal
} from '@components/SchedulePage/ManageScheduleEventMenu/SchedulingCopyCaseModal/SchedulingCopyCaseModal';
import { CopyCaseProvider } from '@components/CaseDetails/CopyCaseContext/CopyCaseContext';
import {
	useInsuranceAuthStatusConfig
} from '@utilities/hooks/useInsuranceAuthStatusConfig/useInsuranceAuthStatusConfig';
import { blockTypeLabel } from '@components/SchedulePage/utilities/blockTypeLabel';
import { SurgeonEventContainer } from '@components/SchedulePage/SurgeonEvent/presentationComponents/SurgeonEventContainer';
import { SurgeonEventTitle } from '@components/SchedulePage/SurgeonEvent/presentationComponents/SurgeonEventTitle';
import { SurgeonEventInfo } from '@components/SchedulePage/SurgeonEvent/presentationComponents/SurgeonEventInfo';
import { SurgeonEventManageCaseMenuContainer }
	from '@components/SchedulePage/SurgeonEvent/presentationComponents/SurgeonEventManageCaseMenuContainer';
import { SurgeonEventFINInfo } from '@components/SchedulePage/SurgeonEvent/presentationComponents/SurgeonEventFINInfo';
import { SurgeonEventProcedureToolTip }
	from '@components/SchedulePage/SurgeonEvent/presentationComponents/SurgeonEventProcedureToolTip';

const useStyles = makeStyles((theme) => ({
	eventIcon: {
		borderRadius: theme.spacing(0.5),
		fill: theme.palette.background.paper,
		position: 'absolute',
		top: -0.4,
		right: -0.4,
		backgroundColor: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		borderStyle: 'solid',
	},
	FINVal: {
		flex: 'auto 1 1',
	},
	FINCopyIcon: {
		height: '18px',
		width: '18px',
	},
}));

export const BlockEvent: React.FC<SurgeonEventProps> = ({ eventInfo, displayMenu = true }) => {
	const mainClass = useStyles();
	const { cardStyles } = useBlockEventConfig(eventInfo);
	const authorizationLabel = useInsuranceAuthStatusConfig(eventInfo?.authorizationStatus);
	const blockProcedureLabelText = blockTypeLabel[eventInfo.blockType] ?? 'Non-Block Procedure';

	return (
		<SurgeonEventContainer style={cardStyles}>
			{eventInfo.isNeedsRevision && (
				<BuildIcon data-field="surgeon-event-icon" className={mainClass.eventIcon} />
			)}
			<SurgeonEventTitle isBlock={!!blockTypeLabel[eventInfo.blockType]}>
				{blockProcedureLabelText}
			</SurgeonEventTitle>
			{eventInfo.isPracticeEvent && (
				<SurgeonEventInfo>Pt: {eventInfo.patient}</SurgeonEventInfo>
			)}
			<SurgeonEventProcedureToolTip title={eventInfo.procedure}>
				{eventInfo.alternateCernerName || eventInfo.procedure}
			</SurgeonEventProcedureToolTip>

			<DisplaySelectedUnitTimeFrame
				timeFrame={{
					start: eventInfo.startDate,
					end: eventInfo.end ? eventInfo.endDate : addMinutes(eventInfo.startDate, 30),
				}}
				variant="body2"
			/>
			<SurgeonEventInfo>{eventInfo.room}</SurgeonEventInfo>
			{authorizationLabel && (
				<SurgeonEventInfo>Auth: {authorizationLabel.label}</SurgeonEventInfo>
			)}
			{eventInfo.fin && (
				<SurgeonEventFINInfo>
					<SurgeonEventInfo>FIN: </SurgeonEventInfo>
					<CopyText
						dataField="blockEvent-copyTextButton"
						successMessage="FIN has been copied"
						className={mainClass.FINVal}
						typographyProps={{ variant: 'body2' }}
						iconButtonProps={{ className: mainClass.FINCopyIcon }}
					>
						{eventInfo.fin}
					</CopyText>
				</SurgeonEventFINInfo>
			)}

			{displayMenu &&
					<SurgeonEventManageCaseMenuContainer>
						<ManageScheduleEventMenu eventInfo={eventInfo} />
						<SchedulingCancelCaseModal eventInfo={eventInfo} />
						<CaseLockModal />
						<CopyCaseProvider>
							<SchedulingCopyCaseModal />
						</CopyCaseProvider>
					</SurgeonEventManageCaseMenuContainer>
			}
		</SurgeonEventContainer>
	);
};
